import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { formatIDR } from "@/components/AppTopBar";

export default function WalletAdjustmentPanel() {
  const [stores, setStores] = useState([]);
  const [adj, setAdj] = useState({ file: null });
  const [adjPreview, setAdjPreview] = useState(null);
  const [adjustments, setAdjustments] = useState([]);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  const load = () => api.get("/marketplace/adjustments").then(({ data }) => setAdjustments(data || [])).catch(() => {});
  useEffect(() => {
    api.get("/stores").then(({ data }) => setStores(data || [])).catch(() => {});
    load();
  }, []);

  const doAdjPreview = async () => {
    setError(""); setNotice(""); setAdjPreview(null);
    if (!adj.file) { setError("Pilih file adjustment."); return; }
    const fd = new FormData(); fd.append("file", adj.file);
    try {
      const { data } = await api.post("/marketplace/adjustment/preview", fd);
      setAdjPreview(data);
    } catch (e) { setError(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const doAdjCommit = async () => {
    setError(""); setNotice("");
    const fd = new FormData(); fd.append("file", adj.file);
    try {
      const { data } = await api.post("/marketplace/adjustment/commit", fd);
      setNotice(`Adjustment: ${data.committed} di-commit, ${data.unresolved_recorded} unresolved, ${data.skipped_duplicates} duplikat.`);
      setAdjPreview(null); setAdj({ file: null }); load();
    } catch (e) { setError(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const mapShopAdj = async (shop, storeId) => {
    setError("");
    try {
      await api.post("/mappings/store", { store_name_source: shop, store_id: storeId });
      setNotice(`Mapping tersimpan untuk '${shop}'. Memproses ulang...`);
      await doAdjPreview();
    } catch (e) { setError(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const doAdjReprocess = async () => {
    setError(""); setNotice("");
    if (!adjPreview?.staging_id) return;
    try {
      const { data } = await api.post(`/marketplace/adjustment/reprocess/${adjPreview.staging_id}`);
      setNotice(`Reprocess: ${data.committed} di-commit, ${data.unresolved_recorded} unresolved, ${data.skipped_duplicates} duplikat.`);
      setAdjPreview(null); setAdj({ file: null }); load();
    } catch (e) { setError(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <>
      <section className="panel" data-testid="adjustment-panel">
        <h2 className="panel-title">Shopee Adjustment Import (auto-map)</h2>
        <p className="hint" style={{ marginTop: -6 }}>Penyesuaian saldo marketplace dari file Shopee. Diproses sebagai bagian dari mutasi wallet.</p>
        {error && <div className="alert-error" data-testid="adj-error">{error}</div>}
        {notice && <div className="alert-info" data-testid="adj-notice">{notice}</div>}
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "flex-end" }}>
          <div><label className="field-label">File Adjustment (.xlsx)</label>
            <input className="field-input" type="file" accept=".xlsx" onChange={(e) => setAdj({ file: e.target.files[0] })} data-testid="adj-file" /></div>
          <button className="btn-primary" onClick={doAdjPreview} disabled={!adj.file} data-testid="adj-preview-btn">Preview (auto-map)</button>
        </div>

        {adjPreview && adjPreview.status === "SHOP_UNMAPPED" && (
          <div className="alert-error" data-testid="adj-shop-unmapped" style={{ marginTop: 10 }}>
            <strong>SHOP_UNMAPPED</strong> — Source Shop: <span className="mono">{adjPreview.shop_source || "(kosong)"}</span> belum dipetakan.
            Petakan sekali di <Link to="/store-mappings" data-testid="adj-goto-mapping">Mapping Toko</Link> (Master Toko + Wallet), lalu klik <strong>Proses Ulang</strong>.
            <div style={{ display: "flex", gap: 8, alignItems: "flex-end", marginTop: 8 }}>
              <div><label className="field-label">Petakan cepat ke Master Toko</label>
                <select className="field-input" defaultValue="" id="adj-unmapped-store-pick" data-testid="adj-map-store">
                  <option value="">-- pilih store --</option>{stores.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
              </div>
              <button className="btn-primary" data-testid="adj-map-save" onClick={() => { const v = document.getElementById("adj-unmapped-store-pick").value; if (v) mapShopAdj(adjPreview.shop_source, v); }}>Simpan Mapping & Proses Ulang</button>
            </div>
            {adjPreview.staging_id && <div style={{ marginTop: 8 }}><button className="btn-primary" onClick={doAdjReprocess} data-testid="adj-reprocess-btn">Proses Ulang (tanpa upload)</button></div>}
          </div>
        )}

        {adjPreview && adjPreview.resolved && (
          <div className="meta-grid" data-testid="adj-resolved" style={{ marginTop: 10, gridTemplateColumns: "repeat(auto-fill,minmax(180px,1fr))", gap: 8 }}>
            <div><span className="meta-label">Source Shop</span><div className="mono" data-testid="adj-resolved-shop">{adjPreview.resolved.shop_source}</div></div>
            <div><span className="meta-label">Master Toko</span><div className="mono" data-testid="adj-resolved-store">{adjPreview.resolved.store_name}</div></div>
            <div><span className="meta-label">Wallet</span><div className="mono" data-testid="adj-resolved-wallet">{adjPreview.resolved.account_name}</div></div>
            <div><span className="meta-label">Mapping</span><div className="mono"><span className="status-pill on">{adjPreview.resolved.mapping}</span></div></div>
          </div>
        )}

        {adjPreview && adjPreview.counts && (
          <div style={{ marginTop: 12 }} data-testid="adj-preview">
            <div className="meta-grid">
              <div><span className="meta-label">Committable</span><span className="mono" data-testid="adj-pv-committable">{adjPreview.counts.committable}</span></div>
              <div><span className="meta-label">Unresolved</span><span className="mono" data-testid="adj-pv-unresolved">{adjPreview.counts.unresolved}</span></div>
              <div><span className="meta-label">Duplikat</span><span className="mono" data-testid="adj-pv-duplicates">{adjPreview.counts.duplicates}</span></div>
              <div><span className="meta-label">Wallet IN</span><span className="mono" data-testid="adj-pv-in">{formatIDR(adjPreview.wallet_in_total)}</span></div>
              <div><span className="meta-label">Wallet OUT</span><span className="mono" data-testid="adj-pv-out">{formatIDR(adjPreview.wallet_out_total)}</span></div>
            </div>
            <p className="hint" data-testid="adj-pv-note">{adjPreview.note}</p>
            <div className="form-actions">
              <button className="btn-primary" onClick={doAdjCommit} disabled={!adjPreview.resolved || (adjPreview.counts.committable === 0 && adjPreview.counts.unresolved === 0)} data-testid="adj-commit-btn">Confirm &amp; Commit ({adjPreview.counts.committable} committable + {adjPreview.counts.unresolved} unresolved)</button>
            </div>
          </div>
        )}

        <div className="table-scroll" style={{ marginTop: 10 }}>
          <table className="data-table" data-testid="adjustments-table">
            <thead><tr><th>Store</th><th>Order Terhubung</th><th>Tipe</th><th>Alasan</th><th>Jumlah</th><th>Tgl Penyesuaian</th><th>Tgl Dana Dilepaskan</th><th>Arah</th><th>Status</th></tr></thead>
            <tbody>
              {adjustments.length === 0 && <tr><td colSpan={9} className="empty-cell" data-testid="adjustments-empty">Belum ada adjustment.</td></tr>}
              {adjustments.map((a) => (
                <tr key={a.id} data-testid={`adjustment-row-${a.id}`}>
                  <td className="mono">{a.store_name}</td><td className="mono">{a.linked_order_id || "-"}</td>
                  <td>{a.adjustment_type || "-"}</td><td className="hint">{a.reason || "-"}</td>
                  <td className="mono">{formatIDR(a.amount)}</td><td>{a.adjustment_date || "-"}</td>
                  <td>{a.release_date || "-"}</td><td className="mono">{a.direction || "-"}</td>
                  <td><span className={`status-pill ${a.status === "COMMITTED" ? "on" : "off"}`}>{a.status}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </>
  );
}
